import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Command, ArrowRight, Clock, Star, ExternalLink, X } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { App } from '../../types';

interface CommandItem {
  id: string;
  type: 'app' | 'action' | 'recent';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

export default function CommandPalette() {
  const {
    commandPaletteOpen,
    toggleCommandPalette,
    apps,
    favoriteAppIds,
    recentAppIds,
    addRecentApp,
    addActivity
  } = useAppStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Handle Cmd+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleCommandPalette();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleCommandPalette]);

  // Focus input when palette opens
  useEffect(() => {
    if (commandPaletteOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [commandPaletteOpen]);

  // Reset state when palette closes
  useEffect(() => {
    if (!commandPaletteOpen) {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [commandPaletteOpen]);

  // Fuzzy search function
  const fuzzyMatch = (str: string, pattern: string): boolean => {
    if (!pattern) return true;

    const patternLower = pattern.toLowerCase();
    const strLower = str.toLowerCase();

    let patternIdx = 0;
    let strIdx = 0;

    while (patternIdx < patternLower.length && strIdx < strLower.length) {
      if (patternLower[patternIdx] === strLower[strIdx]) {
        patternIdx++;
      }
      strIdx++;
    }

    return patternIdx === patternLower.length;
  };

  // Build command items
  const commandItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [];

    if (!searchTerm) {
      // Show recent apps when no search term
      const recentApps = apps.filter((app) => recentAppIds.includes(app.id))
        .sort((a, b) => recentAppIds.indexOf(a.id) - recentAppIds.indexOf(b.id))
        .slice(0, 5);

      recentApps.forEach((app) => {
        items.push({
          id: `recent-${app.id}`,
          type: 'recent',
          title: app.name,
          subtitle: 'Recent',
          icon: <span className="text-xl">{app.icon}</span>,
          onSelect: () => handleLaunchApp(app)
        });
      });

      // Show favorite apps
      const favoriteApps = apps.filter((app) => favoriteAppIds.includes(app.id)).slice(0, 5);
      favoriteApps.forEach((app) => {
        items.push({
          id: `favorite-${app.id}`,
          type: 'app',
          title: app.name,
          subtitle: 'Favorite',
          icon: <Star className="w-5 h-5 text-yellow-500" fill="currentColor" />,
          onSelect: () => handleLaunchApp(app)
        });
      });
    } else {
      // Filter apps by search term using fuzzy matching
      const filteredApps = apps.filter((app) => {
        return (
          fuzzyMatch(app.name, searchTerm) ||
          fuzzyMatch(app.description, searchTerm) ||
          app.tags?.some((tag) => fuzzyMatch(tag, searchTerm))
        );
      });

      filteredApps.forEach((app) => {
        items.push({
          id: `app-${app.id}`,
          type: 'app',
          title: app.name,
          subtitle: app.description,
          icon: <span className="text-xl">{app.icon}</span>,
          onSelect: () => handleLaunchApp(app)
        });
      });

      // Quick actions
      if (fuzzyMatch('settings', searchTerm)) {
        items.push({
          id: 'action-settings',
          type: 'action',
          title: 'Open Settings',
          subtitle: 'Configure your preferences',
          icon: <Command className="w-5 h-5 text-recipe-accent" />,
          onSelect: () => {
            window.location.href = '/settings';
            toggleCommandPalette();
          }
        });
      }

      if (fuzzyMatch('theme', searchTerm)) {
        items.push({
          id: 'action-theme',
          type: 'action',
          title: 'Toggle Theme',
          subtitle: 'Switch between light and dark mode',
          icon: <Command className="w-5 h-5 text-recipe-accent" />,
          onSelect: () => {
            toggleCommandPalette();
          }
        });
      }
    }

    return items;
  }, [apps, searchTerm, favoriteAppIds, recentAppIds]);

  const handleLaunchApp = (app: App) => {
    addRecentApp(app.id);
    addActivity({
      action: 'Launched',
      appName: app.name,
      icon: app.icon
    });

    // Add to recent searches
    if (searchTerm && !recentSearches.includes(searchTerm)) {
      setRecentSearches([searchTerm, ...recentSearches].slice(0, 5));
    }

    window.open(app.url, '_blank');
    toggleCommandPalette();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!commandPaletteOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % commandItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + commandItems.length) % commandItems.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (commandItems[selectedIndex]) {
            commandItems[selectedIndex].onSelect();
          }
          break;
        case 'Escape':
          e.preventDefault();
          toggleCommandPalette();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen, commandItems, selectedIndex, toggleCommandPalette]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (!commandPaletteOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={toggleCommandPalette}
      />

      {/* Palette */}
      <div className="relative w-full max-w-2xl bg-recipe-card border border-recipe-border rounded-xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-recipe-border">
          <Search className="w-5 h-5 text-recipe-text-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Search apps, actions, or type a command..."
            className="flex-1 bg-transparent text-recipe-text placeholder-recipe-text-muted focus:outline-none"
          />
          <div className="flex items-center gap-2">
            <kbd className="px-2 py-1 bg-recipe-bg border border-recipe-border rounded text-xs text-recipe-text-muted font-mono">
              ESC
            </kbd>
            <button
              onClick={toggleCommandPalette}
              className="p-1 hover:bg-recipe-bg rounded"
            >
              <X className="w-4 h-4 text-recipe-text-muted" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-recipe-border scrollbar-track-transparent"
        >
          {commandItems.length === 0 ? (
            <div className="p-8 text-center">
              <Search className="w-12 h-12 text-recipe-text-muted mx-auto mb-3" />
              <p className="text-recipe-text-muted">No results found</p>
              <p className="text-sm text-recipe-text-muted mt-1">
                Try searching for an app name or action
              </p>
            </div>
          ) : (
            <div className="py-2">
              {!searchTerm && recentSearches.length > 0 && (
                <div className="px-4 py-2">
                  <p className="text-xs text-recipe-text-muted uppercase tracking-wide font-semibold">
                    Recent Searches
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {recentSearches.map((search) => (
                      <button
                        key={search}
                        onClick={() => setSearchTerm(search)}
                        className="px-3 py-1 bg-recipe-bg border border-recipe-border rounded-full text-sm text-recipe-text hover:border-recipe-accent transition-colors"
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {commandItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={item.onSelect}
                  className={`w-full flex items-center gap-4 px-4 py-3 transition-colors ${
                    index === selectedIndex
                      ? 'bg-recipe-accent/10 border-l-2 border-recipe-accent'
                      : 'border-l-2 border-transparent hover:bg-recipe-bg'
                  }`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-recipe-bg rounded-lg">
                    {item.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium text-recipe-text truncate">
                      {item.title}
                    </div>
                    {item.subtitle && (
                      <div className="text-xs text-recipe-text-muted truncate">
                        {item.subtitle}
                      </div>
                    )}
                  </div>

                  {/* Type Badge */}
                  {item.type === 'recent' && (
                    <div className="flex items-center gap-1 text-xs text-recipe-text-muted">
                      <Clock className="w-3 h-3" />
                    </div>
                  )}

                  {/* Action Icon */}
                  {index === selectedIndex && (
                    <div className="flex-shrink-0 text-recipe-accent">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-recipe-border bg-recipe-bg/50">
          <div className="flex items-center justify-between text-xs text-recipe-text-muted">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-recipe-card border border-recipe-border rounded font-mono">
                  ↑
                </kbd>
                <kbd className="px-1.5 py-0.5 bg-recipe-card border border-recipe-border rounded font-mono">
                  ↓
                </kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-recipe-card border border-recipe-border rounded font-mono">
                  ↵
                </kbd>
                Select
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Command className="w-3 h-3" />
              <span>+</span>
              <kbd className="px-1.5 py-0.5 bg-recipe-card border border-recipe-border rounded font-mono">
                K
              </kbd>
              <span>to toggle</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
